import React, { useContext, useState } from 'react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import {
  Tooltip,
  SmallBody,
  palettes,
  Table,
  ColumnDefinition,
  RowDefinition,
  Button
} from '@indigo-ag/component-module';
import { AgFinancingFile, AgFinancingDocumentType, AgFinancingFileInternalStatus } from '@indigo-ag/schema';
import i18next from 'core/i18n';

import { documentRequestTypesTranslations, createStylesHook, TableSort, DocumentCard, phase3FileTypes } from 'shared';
import { UserContext } from 'core/components/GateWay/GateWay';
import { date, text, UserUtil } from 'shared/utils';
import { EditDocumentModal } from './EditDocumentModal/EditDocumentModal';
import { defaultSort } from 'features/brazil-grower/pages/BrazilGrowersPage';

const useStyles = createStylesHook(() => ({
  tableLayout: {
    '& div[role="cell"] > div': {
      alignSelf: 'start'
    },
    '& div[role="cell"]:first-child': {
      alignSelf: 'center'
    }
  },
  documentCell: {
    display: 'block',
    width: '100%'
  },
  flex: {
    display: 'flex'
  },
  commentArea: {
    color: palettes.brand.gray[800],
    padding: '12px 0'
  }
}));

interface Props {
  documents: AgFinancingFile[];
  onTableAction?: (action: any) => void;
  sort: TableSort | null;
  selectedRows: string[];
  clearSelectedRows: () => void;
  loading?: boolean;
}
type ColumnId = 'documentType' | 'metadata.comment' | 'uploadedBy.firstName' | 'actions' | 'loadedAt';
interface DefinitionContext {
  isLuchesi: boolean;
  isFlc: boolean;
  classes: any;
  canEditDocument: (file: AgFinancingFile) => boolean;
  handleClickEditDocument: (file: AgFinancingFile) => void;
}
const columnDefinition: ColumnDefinition<AgFinancingFile, ColumnId, DefinitionContext>[] = [
  {
    id: 'documentType',
    header: i18next.t('backgroundCheck_documentType'),
    cell: ({ row, SimpleCell, definitionContext }) => (
      <div className={definitionContext?.classes.documentCell}>
        <SimpleCell label={documentRequestTypesTranslations[row.documentType] || ''} />
        <DocumentCard document={row} />
      </div>
    )
  },
  {
    id: 'metadata.comment',
    header: i18next.t('comment'),
    cell: ({ row, definitionContext }) => (
      <div className={definitionContext?.classes.commentArea}>
        {text.firstLetterUppercase(row.metadata?.comment || '')}
      </div>
    ),
    constraints: {
      min: { rem: 1 },
      max: { fr: 0.8 }
    }
  },
  {
    id: 'uploadedBy.firstName',
    header: i18next.t('uploadedDocumentBy'),
    cell: ({ row, SimpleCell }) => (
      <SimpleCell
        label={row.metadata?.salesforceContentVersionModifiedByName || UserUtil.getUserFullName(row.uploadedBy)}
      />
    )
  },
  {
    id: 'loadedAt',
    header: i18next.t('createdAt'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={date.format({ date: row.loadedAt })} />
  },
  {
    id: 'actions',
    header: i18next.t('actions'),
    cell: ({ row, definitionContext }) => {
      const canEditFile = definitionContext?.canEditDocument(row);
      if (
        (definitionContext?.isLuchesi && row.documentType === AgFinancingDocumentType.INCOME_STATEMENT) ||
        (definitionContext?.isFlc && row.documentType === AgFinancingDocumentType.MATRICULA)
      ) {
        return null;
      }
      return (
        <div className={definitionContext?.classes.flex}>
          {canEditFile && (
            <>
              <Tooltip title={<SmallBody>{i18next.t('editTypeAndName')}</SmallBody>} placement="top-start">
                <Button
                  faIcon={faEdit}
                  variant="tertiary"
                  className={definitionContext?.classes.editButton}
                  onClick={() => definitionContext?.handleClickEditDocument(row)}
                  size="small"
                >
                  {i18next.t('edit')}
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      );
    }
  }
];

const rowDefinition: RowDefinition<AgFinancingFile, ColumnId> = {
  rowId: ({ row }) => row.id
};

const sortable: ColumnId[] = ['documentType', 'metadata.comment', 'uploadedBy.firstName', 'loadedAt'];

const getAgFinancingFileInternalStatusGroupLabel = (internalStatus: AgFinancingFileInternalStatus) => {
  const activeLabel = text.toTitleCase(i18next.t('active'));
  const oldLabel = text.toTitleCase(i18next.t('old'));

  switch (internalStatus) {
    case AgFinancingFileInternalStatus.ACTIVE:
      return activeLabel;
    case AgFinancingFileInternalStatus.OLD:
      return oldLabel;

    default:
      return null;
  }
};

export const BrazilGrowerDocumentsList: React.FunctionComponent<Props> = ({
  documents,
  onTableAction = (action: any) => {},
  sort,
  selectedRows,
  clearSelectedRows,
  loading = false
}) => {
  /**
   * User Context
   */
  const { isFlc, isLuchesi, isInternal, userId, isExternal } = useContext(UserContext);
  /**
   * State
   */
  const [editDocumentModalToggle, setEditDocumentModalToggle] = useState(false);
  const [fileSelected, setFileSelected] = useState<AgFinancingFile | undefined>();

  /**
   * Styles
   */
  const classes = useStyles();

  const openEditDocumentModalToggle = () => setEditDocumentModalToggle(true);

  const closeEditDocumentModalToggle = () => setEditDocumentModalToggle(false);

  const handleClickEditDocument = (file: AgFinancingFile) => {
    setFileSelected(file);
    openEditDocumentModalToggle();
    clearSelectedRows();
  };

  const canEditDocument = (file: AgFinancingFile): boolean => {
    if (isInternal) {
      return true;
    }

    return (
      isExternal &&
      (userId === file.uploadedBy.id ||
        (isLuchesi &&
          [
            ...phase3FileTypes,
            AgFinancingDocumentType.REGISTERED_CONTRACT,
            AgFinancingDocumentType.SALESFORCE_DOCUMENT
          ].includes(file.documentType)))
    );
  };

  return (
    <div className={classes.tableLayout}>
      {fileSelected && (
        <EditDocumentModal
          closeModal={closeEditDocumentModalToggle}
          file={fileSelected}
          isOpen={editDocumentModalToggle}
        />
      )}
      <Table
        data={documents}
        label="documents"
        columnDefinition={columnDefinition}
        rowDefinition={rowDefinition}
        onTableAction={onTableAction}
        sort={sort || defaultSort}
        sortable={sortable}
        selectable={true}
        groupBy={({ row }) => getAgFinancingFileInternalStatusGroupLabel(row.internalStatus)}
        selectedRows={{ mode: 'pick', values: new Set(selectedRows) }}
        definitionContext={{
          isLuchesi,
          isFlc,
          classes,
          canEditDocument,
          handleClickEditDocument
        }}
        loading={loading}
      />
    </div>
  );
};
